import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Lightbulb, Loader2, Plus, X, Trash2, Play, ShieldCheck, Pause, PlayCircle, Lock, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';
import ProcessHelp from '@/components/ProcessHelp';

const PRIORITY_STYLE = {
  high: 'bg-rose-500/10 border-rose-500/40 text-rose-600 dark:text-rose-300',
  medium: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-300',
  low: 'bg-muted/40 border-border/40 text-muted-foreground',
};
const SEV_STYLE = {
  high: 'bg-rose-500/15 border-rose-500/40 text-rose-600 dark:text-rose-300',
  medium: 'bg-amber-500/15 border-amber-500/40 text-amber-600 dark:text-amber-300',
  low: 'bg-muted/40 border-border/40 text-muted-foreground',
};
const SIGNAL_LABEL = { loss_ratio: 'Loss ratio', frequency: 'Frequency', reserve_drift: 'Reserve drift', any: 'Any signal' };

const EMPTY_RULE = {
  name: '', description: '', signal_type: 'loss_ratio', severity_at_least: 'warning', scope: '',
  action: '', category: 'pricing', rationale: '', expected_impact: '', priority: 'high', material: false,
};

export default function RecommendationsPage() {
  const canManage = useCan('measures:certify');
  const [params] = useSearchParams();
  const attachScenario = params.get('attach_scenario') || '';
  const qpModel = params.get('model_id') || '';
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState('');
  const [options, setOptions] = useState({ categories: [], priorities: [], severities: [], signal_types: [], guardrail: '' });
  const [rules, setRules] = useState([]);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [busy, setBusy] = useState(new Set());
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_RULE);
  const [scenarios, setScenarios] = useState([]);
  const [submitFor, setSubmitFor] = useState('');
  const [submitNote, setSubmitNote] = useState('');
  const [submitScn, setSubmitScn] = useState('');

  useEffect(() => {
    api.getSemanticModels().then((r) => {
      const ms = r.models || r || [];
      setModels(ms);
      if (!modelId && ms.length) setModelId(qpModel || ms[0].model_id);
    }).catch(() => {});
    api.getRecommendationOptions().then(setOptions).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const [rr, rc, sc] = await Promise.all([
        api.getRecommendationRules(modelId), api.getAdvisoryRecommendations(modelId), api.getScenarios(modelId),
      ]);
      setRules(rr.rules || []);
      setRecs(rc.recommendations || []);
      setScenarios(sc.scenarios || []);
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [modelId]);

  useEffect(() => { load(); }, [load]);

  const withBusy = async (key, fn, okMsg) => {
    setBusy((p) => new Set(p).add(key));
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.message || 'Action failed'); }
    finally { setBusy((p) => { const s = new Set(p); s.delete(key); return s; }); }
  };

  const createRule = () => withBusy('create', async () => {
    if (!modelId) throw new Error('Pick a model first');
    if (!form.name.trim()) throw new Error('Name is required');
    if (!form.action.trim()) throw new Error('Recommended action is required');
    await api.createRecommendationRule({
      model_id: modelId, name: form.name.trim(), description: form.description.trim(),
      trigger: { signal_type: form.signal_type, severity_at_least: form.severity_at_least, scope: form.scope.trim() },
      recommendation: {
        action: form.action.trim(), category: form.category, rationale: form.rationale.trim(),
        expected_impact: form.expected_impact.trim(), priority: form.priority, material: form.material,
      },
    });
    setForm(EMPTY_RULE);
    setShowNew(false);
  }, 'Rule created');

  const toggleRule = (r) => withBusy(`t-${r.rule_id}`, () => api.toggleRecommendationRule(r.rule_id));
  const deleteRule = (r) => withBusy(`d-${r.rule_id}`, () => api.deleteRecommendationRule(r.rule_id), 'Rule deleted');

  const evaluate = async () => {
    if (!modelId) return;
    setEvaluating(true);
    try {
      const r = await api.evaluateRecommendations(modelId);
      setRecs(r.recommendations || []);
      toast.success(`${(r.recommendations || []).length} advisory recommendation(s) from ${r.evaluated_rules} active rule(s)`);
    } catch (e) {
      toast.error(e.message || 'Evaluation failed');
    } finally {
      setEvaluating(false);
    }
  };

  const submitApproval = (rec) => withBusy(`appr-${rec.rec_id}`, async () => {
    await api.submitForApproval(rec.rec_id, submitNote, submitScn);
    setSubmitFor(''); setSubmitNote(''); setSubmitScn('');
  }, 'Submitted for approval');

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="recommendations-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-heading font-semibold flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-400" /> Recommendation Rules
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Turn early-warning signals into governed, advisory recommendations — with rationale, expected impact and the run they were based on.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ProcessHelp
            testId="recommendations-help"
            title="Recommendation Rules & Approvals"
            section="recommendation-rules"
            intro="Turn a risk signal into a governed advisory recommendation. Nothing auto-executes — material outcomes route to a named human approver with full audit."
            steps={[
              'Give a rule a name and pick the signal condition that should trigger it.',
              'Choose the advisory action to recommend and write the reviewer guidance; mark whether the outcome is material.',
              'Save to activate, or Pause a rule to stop it firing without deleting it.',
              'Material recommendations go to Recommendation Approvals, where a different person (four-eyes) approves or rejects.',
              'From the Scenario Simulator you can attach a saved scenario as rationale for a material recommendation.',
            ]}
          />
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="h-8 rounded-md border border-border/50 bg-background px-2 text-xs" data-testid="rec-model-select">
            {models.map((m) => <option key={m.model_id} value={m.model_id}>{m.name || m.model_id}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2" data-testid="rec-guardrail">
        <Lock className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
        <p className="text-[11px] text-emerald-700 dark:text-emerald-300">{options.guardrail || 'Advisory only — recommendations never auto-execute. Material actions route to human approval + audit.'}</p>
      </div>

      {attachScenario ? (
        <div className="flex items-center gap-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-3 py-2" data-testid="rec-attach-banner">
          <ShieldCheck className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
          <p className="text-[11px] text-cyan-700 dark:text-cyan-300">
            Attaching scenario <span className="font-medium">{(scenarios.find((s) => s.scenario_id === attachScenario) || {}).name || 'selected scenario'}</span> — open a material recommendation below and Submit for approval; it will be pre-attached as rationale.
          </p>
        </div>
      ) : null}

      {/* Advisory recommendations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" /> Advisory recommendations
          </h2>
          <Button size="sm" onClick={evaluate} disabled={evaluating || !modelId} className="gap-1 text-xs h-8" data-testid="rec-evaluate">
            {evaluating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Evaluate now
          </Button>
        </div>
        {recs.length === 0 ? (
          <div className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border/40 rounded-xl" data-testid="rec-empty">
            No advisory recommendations. Add active rules below, then <span className="text-foreground">Evaluate now</span> to generate suggestions from current signals.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="rec-list">
            {recs.map((r) => (
              <div key={r.rec_id} className={`rounded-xl border p-3 space-y-2 ${r.priority === 'high' ? 'border-rose-500/30' : 'border-border/40'} bg-card/40`}
                data-testid={`rec-item-${r.rec_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className={`text-[9px] ${PRIORITY_STYLE[r.priority] || ''}`}>{r.priority} priority</Badge>
                    <Badge variant="outline" className="text-[9px] border-border/40 text-muted-foreground capitalize">{r.category}</Badge>
                    {r.material && (
                      <Badge variant="outline" className="text-[9px] border-rose-500/40 text-rose-600 dark:text-rose-300 gap-1" data-testid={`rec-material-${r.rec_id}`}>
                        <ShieldCheck className="w-2.5 h-2.5" /> Material · needs approval
                      </Badge>
                    )}
                  </div>
                  <span className="text-[10px] text-muted-foreground">{r.rule_name}</span>
                </div>
                <p className="text-sm text-foreground font-medium">{r.action}</p>
                {r.rationale && <p className="text-[11px] text-muted-foreground"><span className="text-foreground">Why:</span> {r.rationale}</p>}
                {r.expected_impact && <p className="text-[11px] text-muted-foreground"><span className="text-foreground">Expected impact:</span> {r.expected_impact}</p>}
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground border-t border-border/30 pt-1.5">
                  <span>Triggered by</span>
                  <Badge variant="outline" className="text-[8px] border-border/40">{SIGNAL_LABEL[r.trigger_match?.signal_type] || r.trigger_match?.signal_type}</Badge>
                  <span className="text-foreground font-medium">{r.trigger_match?.member} {r.trigger_match?.display}</span>
                  <Badge variant="outline" className={`text-[8px] ${SEV_STYLE[r.trigger_match?.severity] || ''}`}>{r.trigger_match?.severity}</Badge>
                </div>
                {r.material && canManage && (
                  r.approval_status ? (
                    <Badge variant="outline" className="text-[9px] border-emerald-500/30 text-emerald-600 dark:text-emerald-300" data-testid={`rec-approval-status-${r.rec_id}`}>
                      {r.approval_status === 'pending_approval' ? 'Submitted for approval' : `Approval: ${r.approval_status}`}
                    </Badge>
                  ) : submitFor === r.rec_id ? (
                    <div className="space-y-1.5 border-t border-border/30 pt-2" data-testid={`rec-submit-panel-${r.rec_id}`}>
                      <select value={submitScn} onChange={(e) => setSubmitScn(e.target.value)}
                        className="w-full h-7 px-2 rounded border border-border/40 bg-background text-[11px]" data-testid={`rec-submit-scenario-${r.rec_id}`}>
                        <option value="">Attach a saved scenario as rationale (optional)…</option>
                        {scenarios.map((s) => <option key={s.scenario_id} value={s.scenario_id}>{s.name} — {s.headline}</option>)}
                      </select>
                      <input value={submitNote} onChange={(e) => setSubmitNote(e.target.value)} placeholder="Note to the approver (optional)"
                        className="w-full h-7 px-2 rounded border border-border/40 bg-background text-[11px]" data-testid={`rec-submit-note-${r.rec_id}`} />
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => submitApproval(r)} disabled={busy.has(`appr-${r.rec_id}`)}
                          className="h-6 text-[10px] gap-1 flex-1 border-emerald-500/40 text-emerald-600 dark:text-emerald-300" variant="outline" data-testid={`rec-submit-confirm-${r.rec_id}`}>
                          {busy.has(`appr-${r.rec_id}`) ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <ShieldCheck className="w-2.5 h-2.5" />} Confirm submit
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setSubmitFor(''); setSubmitScn(''); setSubmitNote(''); }} className="h-6 text-[10px]" data-testid={`rec-submit-cancel-${r.rec_id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => { setSubmitFor(r.rec_id); setSubmitScn(attachScenario || ''); setSubmitNote(''); }}
                      className="h-6 text-[10px] gap-1 border-emerald-500/40 text-emerald-600 dark:text-emerald-300 w-full" data-testid={`rec-submit-approval-${r.rec_id}`}>
                      <ShieldCheck className="w-2.5 h-2.5" /> Submit for approval
                    </Button>
                  )
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rules */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-heading font-semibold text-foreground">Rules ({rules.length})</h2>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setShowNew((v) => !v)} className="gap-1 text-xs h-7" data-testid="rec-new">
              <Plus className="w-3 h-3" /> New rule
            </Button>
          )}
        </div>

        {showNew && canManage && (
          <div className="rounded-xl border border-border/40 bg-card/30 p-4 space-y-3" data-testid="rec-new-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-muted-foreground">Rule name</Label>
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. High loss-ratio → review pricing"
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-name" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Scope (dimension member, optional)</Label>
                <input value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
                  placeholder="e.g. LIAB (blank = any)"
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-scope" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">When signal</Label>
                <select value={form.signal_type} onChange={(e) => setForm((f) => ({ ...f, signal_type: e.target.value }))}
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-signal">
                  {(options.signal_types || []).map((t) => <option key={t} value={t}>{SIGNAL_LABEL[t] || t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Fires at</Label>
                <select value={form.severity_at_least} onChange={(e) => setForm((f) => ({ ...f, severity_at_least: e.target.value }))}
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-severity">
                  {(options.severities || []).map((s) => <option key={s} value={s}>{s} or worse</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Recommended action</Label>
                <input value={form.action} onChange={(e) => setForm((f) => ({ ...f, action: e.target.value }))}
                  placeholder="e.g. Review rate adequacy for this line"
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-action" />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Category</Label>
                <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5 capitalize" data-testid="rec-form-category">
                  {(options.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Priority</Label>
                <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-priority">
                  {(options.priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Expected impact</Label>
                <input value={form.expected_impact} onChange={(e) => setForm((f) => ({ ...f, expected_impact: e.target.value }))}
                  placeholder="e.g. ~3 pts loss-ratio improvement over 12m"
                  className="w-full h-8 px-2 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-impact" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-muted-foreground">Rationale</Label>
              <textarea value={form.rationale} onChange={(e) => setForm((f) => ({ ...f, rationale: e.target.value }))}
                rows={2} className="w-full px-2 py-1 rounded border border-border/40 bg-background text-xs mt-0.5" data-testid="rec-form-rationale" />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-foreground cursor-pointer" data-testid="rec-form-material-label">
              <input type="checkbox" checked={form.material} onChange={(e) => setForm((f) => ({ ...f, material: e.target.checked }))} data-testid="rec-form-material" />
              Material action (routes to human approval + audit before anything is done)
            </label>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={createRule} disabled={busy.has('create')} className="h-8 text-xs" data-testid="rec-form-submit">
                {busy.has('create') ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create rule'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowNew(false)} className="h-8 text-xs"><X className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        ) : rules.length === 0 ? (
          <div className="text-xs text-muted-foreground py-6 text-center" data-testid="rec-rules-empty">No rules yet for this model.</div>
        ) : (
          <div className="space-y-2" data-testid="rec-rules-list">
            {rules.map((r) => (
              <div key={r.rule_id} className="rounded-xl border border-border/40 bg-card/30 p-3 flex items-start justify-between gap-3" data-testid={`rec-rule-${r.rule_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <Badge variant="outline" className={`text-[9px] ${r.status === 'active' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-300' : 'bg-muted/40 border-border/40 text-muted-foreground'}`} data-testid={`rec-rule-status-${r.rule_id}`}>
                      {r.status}
                    </Badge>
                    <Badge variant="outline" className={`text-[9px] ${PRIORITY_STYLE[r.recommendation?.priority] || ''}`}>{r.recommendation?.priority}</Badge>
                    {r.recommendation?.material && <Badge variant="outline" className="text-[9px] border-rose-500/40 text-rose-600 dark:text-rose-300">material</Badge>}
                  </div>
                  <p className="text-sm text-foreground font-medium">{r.name}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    When <span className="text-foreground">{SIGNAL_LABEL[r.trigger?.signal_type] || r.trigger?.signal_type}</span> is <span className="text-foreground">{r.trigger?.severity_at_least}</span>{r.trigger?.scope ? ` for ${r.trigger.scope}` : ''} → <span className="text-foreground">{r.recommendation?.action}</span>
                  </p>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => toggleRule(r)} disabled={busy.has(`t-${r.rule_id}`)} className="h-6 text-[10px] gap-1" data-testid={`rec-toggle-${r.rule_id}`}>
                      {r.status === 'active' ? <><Pause className="w-2.5 h-2.5" /> Pause</> : <><PlayCircle className="w-2.5 h-2.5" /> Activate</>}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteRule(r)} disabled={busy.has(`d-${r.rule_id}`)} className="h-6 text-[10px] text-rose-500" data-testid={`rec-delete-${r.rule_id}`}>
                      <Trash2 className="w-2.5 h-2.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export { RecommendationsPage };
